// One accessible dialog behaviour for every overlay on the site: the bag, the phone menu, search,
// the size guide and the photo lightbox. Keeps focus inside, closes on Escape (top-most first),
// hides the page behind it from screen readers and stops it scrolling, and puts focus back afterwards.
const stack = [];
const FOCUSABLE = 'a[href]:not([tabindex="-1"]),button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

let bound = false;
function bind(doc) {
  if (bound) return; bound = true;
  doc.addEventListener('keydown', (e) => {
    const top = stack[stack.length - 1]; if (!top) return;
    if (e.key === 'Escape') { e.preventDefault(); top.close(); return; }
    if (e.key !== 'Tab') return;
    const f = top.focusables(); if (!f.length) { e.preventDefault(); return; }
    const a = f[0], z = f[f.length - 1], cur = doc.activeElement;
    if (!top.el.contains(cur)) { e.preventDefault(); (e.shiftKey ? z : a).focus(); }
    else if (e.shiftKey && cur === a) { e.preventDefault(); z.focus(); }
    else if (!e.shiftKey && cur === z) { e.preventDefault(); a.focus(); }
  });
}

/**
 * @param {HTMLElement} el     the dialog element
 * @param {object} o
 *   veil         element behind the dialog (gets .on), clicking it closes
 *   mode         'class' (toggle .on + inert, for sliding panels), 'hidden' (toggle [hidden]) or
 *                'fade' ([hidden] removed, then .on next frame; .on removed, then [hidden] after the fade)
 *   initialFocus () => element to focus on open (default: first focusable)
 *   onOpen/onClose callbacks; trigger: element whose aria-expanded follows the state
 */
export function createDialog(el, { veil = null, mode = 'class', initialFocus = null, onOpen = null, onClose = null, trigger = null, lockScroll = true, doc = document } = {}) {
  bind(doc);
  let back = null, hideT = 0;
  const focusables = () => [...el.querySelectorAll(FOCUSABLE)].filter(x => x.getClientRects().length);
  const isOpen = () => stack.includes(api);
  function open() {
    if (isOpen()) return;
    back = doc.activeElement;
    clearTimeout(hideT);
    if (mode === 'hidden') el.hidden = false;
    else if (mode === 'fade') { el.hidden = false; requestAnimationFrame(() => el.classList.add('on')); }
    else { el.inert = false; el.classList.add('on'); }
    el.setAttribute('aria-hidden', 'false');
    veil?.classList.add('on');
    if (lockScroll) doc.documentElement.classList.add('is-locked');
    trigger?.setAttribute('aria-expanded', 'true');
    stack.push(api);
    onOpen?.();
    const target = initialFocus?.() || focusables()[0];
    // after the slide-in starts, so the browser doesn't scroll the off-screen panel into view
    requestAnimationFrame(() => target?.focus({ preventScroll: true }));
  }
  function close() {
    if (!isOpen()) return;
    stack.splice(stack.indexOf(api), 1);
    if (mode === 'hidden') el.hidden = true;
    else if (mode === 'fade') { el.classList.remove('on'); hideT = setTimeout(() => { el.hidden = true; }, 200); }
    else { el.classList.remove('on'); el.inert = true; }
    el.setAttribute('aria-hidden', 'true');
    veil?.classList.remove('on');
    if (lockScroll && !stack.length) doc.documentElement.classList.remove('is-locked');
    trigger?.setAttribute('aria-expanded', 'false');
    onClose?.();
    if (back && back.isConnected && back.focus) back.focus({ preventScroll: true });
  }
  veil?.addEventListener('click', close);
  const api = { el, open, close, isOpen, focusables, toggle: () => (isOpen() ? close() : open()) };
  return api;
}
